export type SplitType = "total" | "organic" | "paid" | "perCreator";
export type PlatformType = "all" | "tiktok" | "instagram" | "facebook" | "youtube";

export interface MetricConfig {
	id: string;
	label: string;
	description: string;
	formula: string;
	icon: string;
	visible: boolean;
	isPercentage?: boolean;
	isRatio?: boolean;
	isTime?: boolean;
}

export interface PlatformConfig {
	platform: PlatformType;
	splits: {
		[key in SplitType]?: MetricConfig[];
	};
}

export const METRIC_CONFIGS: PlatformConfig[] = [
	{
		platform: "all",
		splits: {
			total: [
				{
					id: "views",
					label: "Views",
					description: "The total number of times all videos in the project have been watched",
					formula:
						"=SUM(Total Views of all platforms)\n\nTotal Views = Sum of all Organic Views + Paid Views across all platforms",
					icon: "/icon/view.svg",
					visible: true,
				},
				{
					id: "engagements",
					label: "Engagements",
					description:
						"The total interactions on content, including likes, comments, shares, reposts, and saves. Indicates how compelling content is to viewers.",
					formula:
						"=SUM(Total Engagements of all platforms)\n\nTotal Engagements = Sum of all platform engagements across the project",
					icon: "/icon/message-2.svg",
					visible: true,
				},
				{
					id: "avgEngagementRate",
					label: "Avg. Engagement Rate",
					description:
						"The percentage of audience interaction with content. Measures how engaging content is among users who saw it organically (without paid promotion)",
					formula: "=AVE(Average ER% of all platforms)\n\nAverage of Platform ER% across the project",
					icon: "/icon/message-2.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "followerReach",
					label: "Follower Reach",
					description: "The number of unique followers who saw the content",
					formula: "=SUM(Follower Reach of all platforms)\n\nFollower Reach = Video Views/ Follower Count",
					icon: "/icon/report_followers.svg",
					visible: true,
					isRatio: true,
				},
				{
					id: "followerRelevance",
					label: "Avg. Follower Relevance",
					description:
						"Indicates how much of the content reach comes from existing followers vs non-followers",
					formula:
						"=AVE(Average Follower Relevance % of all platforms)\n\nFollower Relevance =(Total Engagements / Follower Count) * 100%",
					icon: "/icon/report_followers.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "saveToViewRatio",
					label: "Avg. Save-to-View ratio",
					description:
						"Shows how often viewers save the content relative to how many views it gets. Shows if users have organic intent",
					formula:
						"=AVE(Save-to-View Ratio of all platforms)\n\nSave to View Ratio = Total Saves / Total Views *100%",
					icon: "/icon/bookmark.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "shareToViewRatio",
					label: "Avg. Share-to-View ratio",
					description: "Spreading the brand message",
					formula:
						"=AVE(Share-to-View Ratio of all platforms)\n\nCreator Share-to-View Ratio = Total Shares / Total Views *100%",
					icon: "/icon/report_share.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "likes",
					label: "Likes",
					description: "The total number of times content was liked",
					formula: "=SUM(Total Likes of all platforms)",
					icon: "/icon/thumbs-up.svg",
					visible: true,
				},
				{
					id: "comments",
					label: "Comments",
					description: "The total number of times content was commented on",
					formula: "=SUM(Total Comments of all platforms)",
					icon: "/icon/comments.svg",
					visible: true,
				},
				{
					id: "shares",
					label: "Shares",
					description: "The total number of times content was shared",
					formula: "=SUM(Total Shares of all platforms)",
					icon: "/icon/report_share.svg",
					visible: true,
				},
				{
					id: "saves",
					label: "Saves",
					description: "The total number of times content was saved",
					formula: "=SUM(Total Saves of all platforms)",
					icon: "/icon/bookmark.svg",
					visible: true,
				},
				{
					id: "reposts",
					label: "Reposts",
					description: "The total number of times content was reposted (IG metric ONLY)",
					formula: "Total Reposts of all creators in the IG platform",
					icon: "/icon/report_repost.svg",
					visible: false,
				},
				{
					id: "avgWatchTime",
					label: "Avg. Watch Time",
					description:
						"The average of time viewers spend watching a video. It’s calculated by dividing the total play time of the video by the total number of video views.",
					formula: "=AVE(Average Watch Time of the TikTok platform)\n\nAWT Data is pulled from TTO",
					icon: "/icon/report_watch.svg",
					visible: false,
					isTime: true,
				},
				{
					id: "avgRetentionRate",
					label: "Avg. Retention Rate",
					description: "Shows how engaging video was",
					formula:
						"=AVE(Retention Rate% of all creators of the TikTok platform)\n\nRetention Rate = Average Watch time/Video Duration",
					icon: "/icon/report_watch.svg",
					visible: false,
					isPercentage: true,
				},
				{
					id: "sixSecondViewsPercent",
					label: "6 seconds Views %",
					description: "Percentage of views that lasted at least 6 seconds long.",
					formula:
						"=AVE(Average 6s Views% of all platforms)\n\n6s Views % - Check TikTok One - Usually reported as a percentage %",
					icon: "/icon/view.svg",
					visible: false,
					isPercentage: true,
				},
				{
					id: "sixSecondViewsNo",
					label: "6 seconds Views (No.)",
					description: "Percentage of views that lasted at least 6 seconds long.",
					formula: "=SUM(All 6s views of all platforms)\n\n6s Views = Total Views * 6s Views %",
					icon: "/icon/view.svg",
					visible: false,
				},
				{
					id: "vtr",
					label: "Avg. Viewthrough Rate",
					description:
						"Percentage of viewers who watch a video ad from start to finish (or to a specified milestone) versus those who skip it",
					formula:
						"=AVE(Average Creator VTR% of all platforms)\n\nVTR% = 6s Views % / Total Views %\n\nPaid: Impressions instead of Views",
					icon: "/icon/report_viewthrough.svg",
					visible: false,
					isPercentage: true,
				},
			],
		},
	},
	{
		platform: "tiktok",
		splits: {
			total: [
				{
					id: "views",
					label: "Total Views",
					description: "The total number of times all videos in the project have been watched",
					formula:
						"=SUM(Views of all Creators in the campaign)\n\nCreator Views = Check the creator’s public profile data, Data is also available on TTO",
					icon: "/icon/view.svg",
					visible: true,
				},
				{
					id: "engagements",
					label: "Total Engagements",
					description:
						"The total interactions on organic content, including likes, comments, shares, and saves. Indicates how compelling content is to viewers.",
					formula:
						"=SUM (Total Engagements of all Creators in the campaign)\n\nTotal Engagements = Sum of likes, comments, shares, saves",
					icon: "/icon/message-2.svg",
					visible: true,
				},
				{
					id: "avgEngagementRate",
					label: "Avg. Engagement Rate",
					description:
						"The percentage of audience interaction with content. Measures how engaging content is among users who saw it organically (without paid promotion)",
					formula: "=AVE (ER% of all Creators in the campaign)",
					icon: "/icon/message-2.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "cpv",
					label: "CPV (Cost Per View)",
					description: "The average amount of money you've spent per 1,000 views.",
					formula: "(creator cost/total views)*1000",
					icon: "/icon/report_cost.svg",
					visible: true,
				},
				{
					id: "cpe",
					label: "CPE (Cost Per Engagement)",
					description: "The average amount of money you've spent per 1,000 engagements.",
					formula: "(creator cost/total engagements)*1000",
					icon: "/icon/report_cost.svg",
					visible: true,
				},
				{
					id: "followerReach",
					label: "Total Follower Reach",
					description: "The number of unique followers who saw the content",
					formula: "Follower Reach = Video Views/ Follower Count",
					icon: "/icon/report_followers.svg",
					visible: true,
					isRatio: true,
				},
				{
					id: "followerRelevance",
					label: "Avg. Follower Relevance",
					description:
						"Indicates how much of the content reach comes from existing followers vs non-followers",
					formula: "Follower Relevance =(Total Engagements / Follower Count) * 100",
					icon: "/icon/report_followers.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "saveToViewRatio",
					label: "Avg. Save-to-View Ratio",
					description:
						"Shows how often viewers save the content relative to how many views it gets. Shows if users have organic intent",
					formula: "Save to View Ratio = Total Saves / Views *100",
					icon: "/icon/bookmark.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "shareToViewRatio",
					label: "Avg. Share-to-View Ratio",
					description: "Spreading the brand message",
					formula: "Creator Share-to-View Ratio = Total Shares / Views *100",
					icon: "/icon/report_share.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "likes",
					label: "Total Likes",
					description: "The total number of times content was liked",
					formula: "=SUM (Likes of All Creators in the campaign)",
					icon: "/icon/thumbs-up.svg",
					visible: true,
				},
				{
					id: "comments",
					label: "Total Comments",
					description: "The total number of times content was commented o",
					formula: "=SUM (Comments of All Creators in the campaign)",
					icon: "/icon/comments.svg",
					visible: true,
				},
				{
					id: "shares",
					label: "Total Shares",
					description: "The total number of times content was shared",
					formula: "=SUM (Shares of All Creators in the campaign)",
					icon: "/icon/report_share.svg",
					visible: true,
				},
				{
					id: "saves",
					label: "Total Saves",
					description: "The total number of times content was saved",
					formula: "=SUM (Saves of All Creators in the campaign)",
					icon: "/icon/bookmark.svg",
					visible: true,
				},
				{
					id: "avgWatchTime",
					label: "Avg. Watch Time",
					description:
						"The average of time viewers spend watching a video. It’s calculated by dividing the total play time of the video by the total number of video views.",
					formula: "=AVE (AWT of All Creators in the campaign)\n\nAWT = Data is available on TTO",
					icon: "/icon/report_watch.svg",
					visible: false,
					isTime: true,
				},
				{
					id: "avgRetentionRate",
					label: "Avg. Retention Rate",
					description: "Shows how engaging video was",
					formula:
						"=AVE(Retention Rate% of all creators)\n\nRetention Rate % = Average Watch time/Video Duration",
					icon: "/icon/report_watch.svg",
					visible: false,
					isPercentage: true,
				},
				{
					id: "sixSecondViewsPercent",
					label: "6s Views %",
					description: "Percentage of views that lasted at least 6 seconds long.",
					formula:
						"=AVE(All Creator 6s Views%)\n\n6s Views % = Data is available on TTO (Usually reported as a percentage %)",
					icon: "/icon/report_time.svg",
					visible: false,
					isPercentage: true,
				},
				{
					id: "sixSecondViewsNo",
					label: "6s Views No.",
					description: "Percentage of views that lasted at least 6 seconds long.",
					formula: "=SUM(All Creator 6s views)\n\n6s Views = Total Views * 6s Views %",
					icon: "/icon/report_time.svg",
					visible: false,
				},
				{
					id: "vtr",
					label: "Avg. Viewthrough Rate (VTR%)",
					description:
						"Percentage of viewers who watch a video ad from start to finish (or to a specified milestone) versus those who skip it",
					formula: "=AVE (All Creator VTR%)\n\nVTR% = 6s Views % / Total Views %",
					icon: "/icon/report_viewthrough.svg",
					visible: false,
					isPercentage: true,
				},
			],
			organic: [
				{
					id: "organicViews",
					label: "Organic Views",
					description: "The total number of times content was viewed without paid promotion.",
					formula: "Total Views - Paid Views\nData is also available on TTO",
					icon: "/icon/view.svg",
					visible: true,
				},
				{
					id: "organicEngagementRate",
					label: "Organic Engagement Rate %",
					description:
						"Measures how engaging content is among users who saw it organically (without paid promotion)",
					formula: "(Organic Engagements / Organic Views)*100%\nData is also available on TTO",
					icon: "/icon/engagement.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "organicEngagements",
					label: "Organic Engagements",
					description:
						"The total interactions on organic content, including likes, comments, shares, and saves. Indicates how compelling content is to viewers.",
					formula: "Sum of organic likes, organic comments, organic shares, organic saves",
					icon: "/icon/message-2.svg",
					visible: true,
				},
				{
					id: "organicLikes",
					label: "Organic Likes",
					description: "The total number of times content was liked without paid promotion",
					formula: "Data is also available on TTO",
					icon: "/icon/thumbs-up.svg",
					visible: true,
				},
				{
					id: "organicComments",
					label: "Organic Comments",
					description: "The total number of times content was commented on without paid promotion",
					formula: "Check the creator’s public profile data\nData is also available on TTO",
					icon: "/icon/comments.svg",
					visible: true,
				},
				{
					id: "organicShares",
					label: "Organic Shares",
					description: "The total number of times content was shared without paid promotion",
					formula: "Check the creator’s public profile data\nData is also available on TTO",
					icon: "/icon/report_share.svg",
					visible: true,
				},
				{
					id: "organicSaves",
					label: "Organic Saves",
					description: "The total number of times content was saved without paid promotion",
					formula:
						"Check the creator’s public profile data\nData is also available on TTO\n\n***Platform on TTO = Favorites",
					icon: "/icon/bookmark.svg",
					visible: true,
				},
				{
					id: "retentionRate",
					label: "Retention Rate",
					description: "Shows how engaging video was",
					formula: "Retention Rate % = Average Watch time/Video Duration",
					icon: "/icon/report_watch.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "followerReach",
					label: "Follower Reach",
					description: "The number of unique followers who saw the content",
					formula: "Follower Reach = Video Views/ Follower Count",
					icon: "/icon/report_followers.svg",
					visible: true,
					isRatio: true,
				},
				{
					id: "followerRelevance",
					label: "Follower Relevance",
					description:
						"Indicates how much of the content reach comes from existing followers vs non-followers",
					formula: "Follower Relevance =(Total Engagements / Follower Count) * 100",
					icon: "/icon/report_followers.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "saveToViewRatio",
					label: "Save-to-View Ratio",
					description:
						"Shows how often viewers save the content relative to how many views it gets. Shows if users have organic intent",
					formula: "Save to View Ratio = Total Saves / Organic Views *100",
					icon: "/icon/bookmark.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "shareToViewRatio",
					label: "Share-to-View Ratio",
					description: "Spreading the brand message",
					formula: "Share-to-View Ratio = Total Shares / Organic Views *100",
					icon: "/icon/report_share.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "sixSecondViewsPercent",
					label: "6s Views %",
					description: "Percentage of views that lasted at least 6 seconds long.",
					formula: "6s Views % = Data is available on TTO (Usually reported as a percentage %)",
					icon: "/icon/report_time.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "sixSecondViewsNo",
					label: "6s Views No.",
					description: "Percentage of views that lasted at least 6 seconds long.",
					formula: "6s Views = Total Views * 6s Views %",
					icon: "/icon/report_time.svg",
					visible: true,
				},
			],
			paid: [
				{
					id: "impressions",
					label: "Impressions",
					description: "Number of times ads were shown",
					formula: "Data is available on TTAM",
					icon: "/icon/view.svg",
					visible: true,
				},
				{
					id: "paidViews",
					label: "Paid Video Views",
					description: "The total number of times content was viewed through paid promotion.",
					formula: "Data is available on TTO",
					icon: "/icon/view.svg",
					visible: true,
				},
				{
					id: "paidEngagementRate",
					label: "Avg. Paid Engagement Rate %",
					description: "Measures how engaging content is among users who saw it through paid promotion",
					formula: "Data is available on TTO",
					icon: "/icon/engagement.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "paidEngagements",
					label: "Paid Engagements",
					description:
						"The total likes, comments, shares through paid promotion. Indicates how compelling content is to viewers.",
					formula: "Data is available on TTO",
					icon: "/icon/message-2.svg",
					visible: true,
				},
				{
					id: "paidLikes",
					label: "Paid Likes",
					description: "The total number of times content was liked through paid promotion",
					formula: "Data is available on TTO",
					icon: "/icon/thumbs-up.svg",
					visible: true,
				},
				{
					id: "paidComments",
					label: "Paid Comments",
					description: "The total number of times content was commented on through paid promotion",
					formula: "Data is available on TTO",
					icon: "/icon/comments.svg",
					visible: true,
				},
				{
					id: "paidShares",
					label: "Paid Shares",
					description: "The total number of times content was shared through paid promotion",
					formula: "Data is available on TTO",
					icon: "/icon/report_share.svg",
					visible: true,
				},
				{
					id: "vtr",
					label: "Viewthrough Rate (VTR%)",
					description:
						"Percentage of viewers who watch a video ad from start to finish (or to a specified milestone) versus those who skip it",
					formula: "VTR% = 6s Views % / Total Views %",
					icon: "/icon/report_viewthrough.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "cpm",
					label: "Cost Per Mille (CPM)",
					description: "The average amount of money you've spent per 1,000 paid impressions.",
					formula: "=(Total creator cost / total views)*1,000",
					icon: "/icon/report_cost.svg",
					visible: true,
				},
				{
					id: "linkClicks",
					label: "Total Link Clicks",
					description: "The total number of clicks on your ads.",
					formula: "Data is available on TTAM",
					icon: "/icon/engagement.svg",
					visible: true,
				},
				{
					id: "ctr",
					label: "Total CTR (Clickthrough Rate)",
					description: "The percentage of times people saw your ad and performed a click.",
					formula: "Data is available on TTAM\n\n=(Link Clicks/Impressions)*100%",
					icon: "/icon/engagement.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "sixSecondViewsPercent",
					label: "6s Views %",
					description: "Percentage of views that lasted at least 6 seconds long.",
					formula: "6s Views % = Data is available on TTO (Usually reported as a percentage %)",
					icon: "/icon/report_time.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "sixSecondViewsNo",
					label: "6s Views No.",
					description: "Percentage of views that lasted at least 6 seconds long.",
					formula: "6s Views = Total Views * 6s Views %",
					icon: "/icon/report_time.svg",
					visible: true,
				},
				{
					id: "uniqueReach",
					label: "Unique Reach",
					description: "Number of individual people exposed to an ad or content",
					formula: "Data is available on TTAM/TTO",
					icon: "/icon/view.svg",
					visible: true,
				},
			],
		},
	},
	{
		platform: "instagram",
		splits: {
			total: [
				{
					id: "views",
					label: "Total Views",
					description: "The total number of times the video has been watched",
					formula: "=SUM(All creator views)\n\nCreator View = Check the creator’s public profile data",
					icon: "/icon/view.svg",
					visible: true,
				},
				{
					id: "engagements",
					label: "Total Engagements",
					description:
						"The total interactions on content across all creators in the campaign. Indicates how compelling content is to viewers.",
					formula:
						"=SUM(All Creator Engagements)\n\nCreator Engagements = Sum of likes and comments on the video (saves, reposts & shares coming soon)",
					icon: "/icon/message-2.svg",
					visible: true,
				},
				{
					id: "avgEngagementRate",
					label: "Avg. Engagement Rate",
					description:
						"The percentage of audience interaction with content across the campaign. Measures how engaging content is among users who saw it organically (without paid promotion)",
					formula: "=AVE(All Creator ER%)\n\nCreator ER% = (Total Engagements / Total Views)*100%",
					icon: "/icon/message-2.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "cpv",
					label: "CPV (Cost Per View)",
					description: "The average amount of money you've spent per 1,000 views.",
					formula: "(creator cost/total views)*1000",
					icon: "/icon/report_cost.svg",
					visible: true,
				},
				{
					id: "cpe",
					label: "CPE (Cost Per Engagement)",
					description: "The average amount of money you've spent per 1,000 engagements.",
					formula: "(creator cost/total engagements)*1000",
					icon: "/icon/report_cost.svg",
					visible: true,
				},
				{
					id: "followerReach",
					label: "Total Follower Reach",
					description: "The number of unique followers who saw the content",
					formula: "Follower Reach = Video Views/ Follower Count",
					icon: "/icon/report_followers.svg",
					visible: true,
					isRatio: true,
				},
				{
					id: "followerRelevance",
					label: "Avg. Follower Relevance",
					description:
						"Indicates how much of the content reach comes from existing followers vs non-followers",
					formula: "Follower Relevance =(Total Engagements / Follower Count) * 100",
					icon: "/icon/report_followers.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "saveToViewRatio",
					label: "Avg. Save-to-View Ratio",
					description:
						"Shows how often viewers save the content relative to how many views it gets. Shows if users have organic intent",
					formula: "Save to View Ratio = Total Saves / Views *100",
					icon: "/icon/bookmark.svg",
					visible: false,
					isPercentage: true,
				},
				{
					id: "shareToViewRatio",
					label: "Avg. Share-to-View Ratio",
					description: "Spreading the brand message",
					formula: "Creator Share-to-View Ratio = Total Shares / Views *100",
					icon: "/icon/report_share.svg",
					visible: false,
					isPercentage: true,
				},
				{
					id: "likes",
					label: "Total Likes",
					description: "The total number of times content was liked",
					formula: "=SUM(All likes)\n\nLikes = Check the creator’s public profile data",
					icon: "/icon/thumbs-up.svg",
					visible: true,
				},
				{
					id: "comments",
					label: "Total Comments",
					description: "The total number of times content was commented on",
					formula: "=SUM(All comments)\n\ncomments = Check the creator’s public profile data",
					icon: "/icon/comments.svg",
					visible: true,
				},
				{
					id: "saves",
					label: "Total Saves",
					description: "The total number of times content was saved",
					formula: "=SUM(All Saves)\n\nSaves = Check the creator’s public profile data",
					icon: "/icon/bookmark.svg",
					visible: false,
				},
				{
					id: "shares",
					label: "Total Shares",
					description: "The total number of times content was shared",
					formula: "=SUM(All Shares)\n\nShares = Check the creator’s public profile data",
					icon: "/icon/report_share.svg",
					visible: false,
				},
				{
					id: "reposts",
					label: "Total Reposts",
					description: "The total number of times content was reposted (IG only metric)",
					formula: "=SUM(All Reposts)\n\nReposts = Check the creator’s public profile data",
					icon: "/icon/report_repost.svg",
					visible: false,
				},
			],
			perCreator: [
				{
					id: "engagements",
					label: "Total Engagements",
					description:
						"The total interactions on content across all creators in the campaign. Indicates how compelling content is to viewers.",
					formula:
						"=SUM(All Creator Engagements)\n\nCreator Engagements = Sum of like and comments on the video. (saves, reposts & shares coming soon)",
					icon: "/icon/message-2.svg",
					visible: true,
				},
				{
					id: "avgEngagementRate",
					label: "Avg. Engagement Rate",
					description:
						"The percentage of audience interaction with content across the campaign. Measures how engaging content is among users who saw it organically (without paid promotion)",
					formula: "=AVE(All Creator ER%)\n\nCreator ER% = (Total Engagements / Total Views)*100%",
					icon: "/icon/message-2.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "cpv",
					label: "CPV (Cost Per View)",
					description: "The average amount of money you've spent per 1,000 views.",
					formula: "(creator cost/total views)*1000",
					icon: "/icon/report_cost.svg",
					visible: true,
				},
				{
					id: "cpe",
					label: "CPE (Cost Per Engagement)",
					description: "The average amount of money you've spent per 1,000 engagements.",
					formula: "(creator cost/total engagements)*1000",
					icon: "/icon/report_cost.svg",
					visible: true,
				},
			],
		},
	},
	{
		platform: "facebook",
		splits: {
			total: [
				{
					id: "views",
					label: "Total Views",
					description: "The total number of times the video has been watched",
					formula: "=SUM(All creator views)\n\nCreator View = Check the creator’s public profile data",
					icon: "/icon/view.svg",
					visible: true,
				},
				{
					id: "engagements",
					label: "Total Engagements",
					description:
						"The total interactions on content across all creators in the campaign. Indicates how compelling content is to viewers.",
					formula:
						"=SUM(All Creator Engagements)\n\nCreator Engagements = Sum of likes, comments, shares on the video",
					icon: "/icon/message-2.svg",
					visible: true,
				},
				{
					id: "avgEngagementRate",
					label: "Avg. Engagement Rate",
					description:
						"The percentage of audience interaction with content across the campaign. Measures how engaging content is among users who saw it organically (without paid promotion)",
					formula: "=AVE(All Creator ER%)\n\nCreator ER% = (Total Engagements / Total Views)*100%",
					icon: "/icon/message-2.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "cpv",
					label: "CPV (Cost Per View)",
					description: "The average amount of money you've spent per 1,000 views.",
					formula: "(creator cost/total views)*1000",
					icon: "/icon/report_cost.svg",
					visible: true,
				},
				{
					id: "cpe",
					label: "CPE (Cost Per Engagement)",
					description: "The average amount of money you've spent per 1,000 engagements.",
					formula: "(creator cost/total engagements)*1000",
					icon: "/icon/report_cost.svg",
					visible: true,
				},
				{
					id: "likes",
					label: "Total Likes",
					description: "The total number of times content was liked",
					formula: "=SUM(All likes)\n\nLikes = Check the creator’s public profile data",
					icon: "/icon/thumbs-up.svg",
					visible: true,
				},
				{
					id: "comments",
					label: "Total Comments",
					description: "The total number of times content was commented on",
					formula: "=SUM(All comments)\n\ncomments = Check the creator’s public profile data",
					icon: "/icon/comments.svg",
					visible: true,
				},
				{
					id: "saves",
					label: "Total Saves",
					description: "The total number of times content was saved",
					formula: "=SUM(All Saves)\n\nSaves = Check the creator’s public profile data",
					icon: "/icon/bookmark.svg",
					visible: false,
				},
				{
					id: "shares",
					label: "Total Shares",
					description: "The total number of times content was shared",
					formula: "=SUM(All Shares)\n\nShares = Check the creator’s public profile data",
					icon: "/icon/report_share.svg",
					visible: true,
				},
			],
			perCreator: [
				{
					id: "engagements",
					label: "Total Engagements",
					description:
						"The total interactions on content across all creators in the campaign. Indicates how compelling content is to viewers.",
					formula:
						"=SUM(All Creator Engagements)\n\nCreator Engagements = Sum of likes, comments, shares on the video",
					icon: "/icon/message-2.svg",
					visible: true,
				},
				{
					id: "avgEngagementRate",
					label: "Avg. Engagement Rate",
					description:
						"The percentage of audience interaction with content across the campaign. Measures how engaging content is among users who saw it organically (without paid promotion)",
					formula: "=AVE(All Creator ER%)\n\nCreator ER% = (Total Engagements / Total Views)*100%",
					icon: "/icon/message-2.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "cpv",
					label: "CPV (Cost Per View)",
					description: "The average amount of money you've spent per 1,000 views.",
					formula: "(creator cost/total views)*1000",
					icon: "/icon/report_cost.svg",
					visible: true,
				},
				{
					id: "cpe",
					label: "CPE (Cost Per Engagement)",
					description: "The average amount of money you've spent per 1,000 engagements.",
					formula: "(creator cost/total engagements)*1000",
					icon: "/icon/report_cost.svg",
					visible: true,
				},
			],
		},
	},
	{
		platform: "youtube",
		splits: {
			total: [
				{
					id: "views",
					label: "Total Views",
					description: "The total number of times the video has been watched",
					formula: "=SUM(All creator views)\n\nCreator View = Check the creator’s public profile data",
					icon: "/icon/view.svg",
					visible: true,
				},
				{
					id: "engagements",
					label: "Total Engagements",
					description:
						"The total interactions on content across all creators in the campaign. Indicates how compelling content is to viewers.",
					formula:
						"=SUM(All Creator Engagements)\n\nCreator Engagements = Sum of likes and comments on the video (saves & shares coming soon)",
					icon: "/icon/message-2.svg",
					visible: true,
				},
				{
					id: "avgEngagementRate",
					label: "Avg. Engagement Rate",
					description:
						"The percentage of audience interaction with content across the campaign. Measures how engaging content is among users who saw it organically (without paid promotion)",
					formula: "=AVE(All Creator ER%)\n\nCreator ER% = (Total Engagements / Total Views)*100%",
					icon: "/icon/message-2.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "cpv",
					label: "CPV (Cost Per View)",
					description: "The average amount of money you've spent per 1000 views.",
					formula: "(Creator Cost / Total Views)*1000",
					icon: "/icon/report_cost.svg",
					visible: true,
				},
				{
					id: "cpe",
					label: "CPE (Cost Per Engagement)",
					description: "The average amount of money you've spent per 1000 engagements.",
					formula: "(Creator Cost / Total Engagements)*1000",
					icon: "/icon/report_cost.svg",
					visible: true,
				},
				{
					id: "likes",
					label: "Total Likes",
					description: "The total number of times content was liked",
					formula: "=SUM(All likes)\n\nLikes = Check the creator’s public profile data",
					icon: "/icon/thumbs-up.svg",
					visible: true,
				},
				{
					id: "comments",
					label: "Total Comments",
					description: "The total number of times content was commented on",
					formula: "=SUM(All comments)\n\ncomments = Check the creator’s public profile data",
					icon: "/icon/comments.svg",
					visible: true,
				},
				{
					id: "saves",
					label: "Total Saves",
					description: "The total number of times content was saved",
					formula: "=SUM(All Saves)\n\nSaves = Check the creator’s public profile data",
					icon: "/icon/bookmark.svg",
					visible: false,
				},
				{
					id: "shares",
					label: "Total Shares",
					description: "The total number of times content was shared",
					formula: "=SUM(All Shares)\n\nShares = Check the creator’s public profile data",
					icon: "/icon/report_share.svg",
					visible: false,
				},
			],
			perCreator: [
				{
					id: "engagements",
					label: "Total Engagements",
					description:
						"The total interactions on content across all creators in the campaign. Indicates how compelling content is to viewers.",
					formula:
						"=SUM(All Creator Engagements)\n\nCreator Engagements = Sum of likes and comments on the video (saves & shares coming soon)",
					icon: "/icon/message-2.svg",
					visible: true,
				},
				{
					id: "avgEngagementRate",
					label: "Avg. Engagement Rate",
					description:
						"The percentage of audience interaction with content across the campaign. Measures how engaging content is among users who saw it organically (without paid promotion)",
					formula: "=AVE(All Creator ER%)\n\nCreator ER% = (Total Engagements / Total Views)*100%",
					icon: "/icon/message-2.svg",
					visible: true,
					isPercentage: true,
				},
				{
					id: "cpv",
					label: "CPV (Cost Per View)",
					description: "The average amount of money you've spent per 1,000 views.",
					formula: "(creator cost/total views)*1000",
					icon: "/icon/report_cost.svg",
					visible: true,
				},
				{
					id: "cpe",
					label: "CPE (Cost Per Engagement)",
					description: "The average amount of money you've spent per 1,000 engagements.",
					formula: "(creator cost/total engagements)*1000",
					icon: "/icon/report_cost.svg",
					visible: true,
				},
			],
		},
	},
];

export const getMetricsByPlatform = (platform: string, split: SplitType): MetricConfig[] => {
	const platformConfig = METRIC_CONFIGS.find((pc) => pc.platform === platform);
	if (!platformConfig) return [];
	return platformConfig.splits[split] || [];
};
